/**
 * Pure-function utilities for Logbook business logic.
 * Covers note ownership rules and sales/traffic entry validation.
 */

export interface LogbookNote {
  id: number;
  authorId: number;
  content: string;
  createdAt: string;
}

export interface SalesEntry {
  date: string;           // ISO date string
  netSales: number;
  guestCount: number;
  laborHours?: number;
}

/** Formats a user reference from employee note API fields (author, assignToUser). */
export function formatNoteUser(user: unknown): string {
  if (!user) return 'Unknown';

  if (typeof user === 'object') {
    const obj = user as Record<string, unknown>;
    const name = `${obj.firstName || obj.first_name || ''} ${obj.lastName || obj.last_name || ''}`.trim();
    if (name) return name;
    if (obj.firstName) return String(obj.firstName);
    if (obj.id) return `User ${obj.id}`;
  }

  if (typeof user === 'string' && user.includes('/')) {
    return `User ${user.split('/').pop()}`;
  }

  if (typeof user === 'string') return user;

  return 'Unknown';
}

// ─── Note ownership ───────────────────────────────────────────────────────────

/**
 * Returns true if the current user can edit a given note.
 * Rule: author may always edit their own note; a user with logbook delete
 * permission is NOT required to edit — only the author can edit.
 */
export function canEditNote(note: LogbookNote, currentUserId: number): boolean {
  return note.authorId === currentUserId;
}

/**
 * Returns true if the current user can delete a given note.
 * Rule: author can delete their own note; holders of `delete` on `Logbook`
 * can delete any note (regardless of author).
 */
export function canDeleteNote(
  note: LogbookNote,
  currentUserId: number,
  hasLogbookDeletePermission: boolean,
): boolean {
  return note.authorId === currentUserId || hasLogbookDeletePermission;
}

// ─── Sales entry validation ───────────────────────────────────────────────────

/**
 * Validates a sales/traffic entry. Returns error messages (empty = valid).
 */
export function validateSalesEntry(entry: SalesEntry): string[] {
  const errors: string[] = [];

  if (!entry.date?.trim()) {
    errors.push('Date is required.');
  } else if (isNaN(new Date(entry.date).getTime())) {
    errors.push('Date is invalid.');
  }

  if (entry.netSales < 0) {
    errors.push('Net sales cannot be negative.');
  }

  if (!Number.isInteger(entry.guestCount) || entry.guestCount < 0) {
    errors.push('Guest count must be a non-negative integer.');
  }

  if (entry.laborHours !== undefined && entry.laborHours < 0) {
    errors.push('Labor hours cannot be negative.');
  }

  return errors;
}

// ─── Note content validation ──────────────────────────────────────────────────

/** Resolves YYYY-MM-DD for logbook notes from context, localStorage, or today. */
export function resolveLogbookNoteDate(startDate?: string): string {
  const fromContext = startDate?.trim().split('T')[0];
  if (fromContext) return fromContext;

  const stored = localStorage.getItem('logbook-selected-date')?.trim().split('T')[0];
  if (stored) return stored;

  return new Date().toISOString().split('T')[0];
}

/** Validates a note before saving. Returns error messages. */
export function validateNoteContent(content: string): string[] {
  const errors: string[] = [];
  if (!content?.trim()) {
    errors.push('Note content is required.');
  }
  if (content.trim().length > 5000) {
    errors.push('Note exceeds maximum length of 5,000 characters.');
  }
  return errors;
}

/**
 * Converts Quill/HTML note content to a single-line plain string for list previews.
 * Block and break tags become spaces so multi-paragraph notes do not glue words together.
 */
export function stripHtmlToPlainText(html: string | undefined | null): string {
  if (!html) return '';

  return String(html)
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|li|h[1-6]|tr|blockquote|section|article)>/gi, ' ')
    .replace(/<(p|div|li|h[1-6]|tr|blockquote|section|article)[^>]*>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim();
}
